<template>
  <div class="entity-documents" :class="{ 'entity-documents--embedded': embedded }">
    <input
      ref="fileInput"
      type="file"
      class="entity-documents__input"
      multiple
      :accept="accept"
      @change="handleFileSelect"
    />

    <button
      type="button"
      class="entity-documents__add"
      :disabled="uploading || !ownerId"
      @click="openFilePicker"
    >
      <BaseIcon name="plus" size="16" />
      <span>{{ uploading ? $t('documents.uploading') : $t('documents.add') }}</span>
    </button>

    <div v-if="loading" class="entity-documents__loading">
      <div class="entity-documents__spinner"></div>
    </div>

    <div v-else-if="!documents.length" class="entity-documents__empty">
      <BaseIcon name="file" size="20" />
      <div>
        <p class="entity-documents__empty-title">{{ $t('documents.empty_title') }}</p>
        <p class="entity-documents__empty-text">{{ emptyDescription }}</p>
      </div>
    </div>

    <ul v-else class="entity-documents__list">
      <li v-for="document in documents" :key="document.id" class="entity-documents__item">
        <div class="entity-documents__icon">
          <BaseIcon :name="fileIcon(document)" size="16" />
        </div>
        <div class="entity-documents__main">
          <span class="entity-documents__title">{{ document.title }}</span>
          <span class="entity-documents__meta">
            {{ kindLabel(document) }}
            <template v-if="document.file?.size"> · {{ formatSize(document.file.size) }}</template>
            <template v-if="document.created_at"> · {{ formatDate(document.created_at) }}</template>
          </span>
        </div>
        <div class="entity-documents__actions">
          <button
            type="button"
            class="entity-documents__action"
            :title="$t('documents.download')"
            :disabled="downloadingId === document.id"
            @click="downloadDocument(document)"
          >
            <BaseIcon name="export" size="14" />
          </button>
          <button
            v-if="document.deletable"
            type="button"
            class="entity-documents__action entity-documents__action--danger"
            :title="$t('documents.delete')"
            @click="confirmDelete(document)"
          >
            <BaseIcon name="trash" size="14" />
          </button>
        </div>
      </li>
    </ul>
  </div>
</template>

<script>
  import { computed, getCurrentInstance, onMounted, ref, watch } from 'vue';
  import BaseIcon from '@shared/components/BaseIcon.vue';
  import { useToastStore } from '@/store/toastStore';
  import { useUIStore } from '@/store/uiStore';
  import { documentService } from '@/services/documentService';
  import { crmService } from '@/services/crmService';

  const ACCEPT = '.pdf,.png,.jpg,.jpeg,.webp,.gif,.doc,.docx,.xls,.xlsx,.csv,.txt';

  export default {
    name: 'EntityDocuments',
    components: { BaseIcon },
    props: {
      ownerType: {
        type: String,
        required: true,
        validator: (value) => ['deal', 'project'].includes(value),
      },
      ownerId: {
        type: String,
        default: null,
      },
      embedded: {
        type: Boolean,
        default: false,
      },
    },
    setup(props) {
      const instance = getCurrentInstance();
      const $t = instance.appContext.config.globalProperties.$t;
      const toastStore = useToastStore();
      const uiStore = useUIStore();

      const fileInput = ref(null);
      const documents = ref([]);
      const loading = ref(false);
      const uploading = ref(false);
      const downloadingId = ref(null);

      const emptyDescription = computed(() =>
        props.ownerType === 'project'
          ? $t('documents.empty_project')
          : $t('documents.empty_deal')
      );

      const load = async () => {
        if (!props.ownerId) {
          documents.value = [];
          return;
        }
        loading.value = true;
        try {
          const result = await documentService.list(props.ownerType, props.ownerId);
          documents.value = result.documents || [];
        } catch (error) {
          documents.value = [];
          toastStore.addToast({ type: 'error', message: $t('documents.load_failed') });
        } finally {
          loading.value = false;
        }
      };

      const openFilePicker = () => {
        fileInput.value?.click();
      };

      const handleFileSelect = async (event) => {
        const files = Array.from(event.target.files || []);
        if (!files.length || !props.ownerId) return;

        uploading.value = true;
        try {
          for (const file of files) {
            try {
              const created = await documentService.upload(props.ownerType, props.ownerId, file);
              const signed = documents.value.filter((item) => item.kind === 'signed_proposal');
              const uploads = documents.value.filter((item) => item.kind !== 'signed_proposal');
              documents.value = [...signed, created, ...uploads];
            } catch (error) {
              const message = error.response?.data?.error || $t('documents.upload_failed');
              toastStore.addToast({ type: 'error', message });
            }
          }
        } finally {
          uploading.value = false;
          if (fileInput.value) fileInput.value.value = '';
        }
      };

      const downloadDocument = async (document) => {
        if (document.file?.url) {
          window.open(document.file.url, '_blank', 'noopener');
          return;
        }
        if (document.kind !== 'signed_proposal' || !document.proposal_id) return;

        downloadingId.value = document.id;
        try {
          const blob = await crmService.downloadProposalPDF(document.proposal_id, null);
          const url = URL.createObjectURL(blob);
          const link = window.document.createElement('a');
          link.href = url;
          link.download = `${document.title || 'offerte'}.pdf`;
          link.click();
          URL.revokeObjectURL(url);
        } catch (error) {
          toastStore.addToast({ type: 'error', message: $t('documents.download_failed') });
        } finally {
          downloadingId.value = null;
        }
      };

      const confirmDelete = (document) => {
        const modalId = uiStore.addModal(false, {
          title: $t('documents.delete_title'),
          description: $t('documents.delete_description', { name: document.title }),
          submitText: $t('documents.delete_confirm'),
          cancelText: $t('common.cancel'),
          status: 'danger',
          onSubmit: async () => {
            try {
              await documentService.remove(props.ownerType, props.ownerId, document.id);
              documents.value = documents.value.filter((item) => item.id !== document.id);
              uiStore.closeModal(modalId);
              toastStore.addToast({ type: 'success', message: $t('documents.deleted') });
            } catch (error) {
              uiStore.closeModal(modalId);
              const message = error.response?.data?.error || $t('documents.delete_failed');
              toastStore.addToast({ type: 'error', message });
            }
          },
          onCancel: () => uiStore.closeModal(modalId),
        });
      };

      const kindLabel = (document) => {
        if (document.kind === 'signed_proposal') {
          if (document.proposal_type === 'proforma') return $t('documents.signed_proforma');
          if (document.proposal_type === 'additional') return $t('documents.signed_additional');
          return $t('documents.signed_proposal');
        }
        if (props.ownerType === 'project' && document.origin === 'deal') {
          return $t('documents.from_deal');
        }
        if (props.ownerType === 'deal' && document.origin === 'project') {
          return $t('documents.from_project');
        }
        return $t('documents.uploaded');
      };

      const fileIcon = (document) => {
        const mime = document.file?.mime_type || '';
        if (mime.startsWith('image/')) return 'image';
        return 'file';
      };

      const formatSize = (bytes) => {
        if (!bytes) return '';
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
      };

      const formatDate = (value) => {
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return '';
        return date.toLocaleDateString('nl-NL', {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
        });
      };

      onMounted(load);
      watch(
        () => [props.ownerType, props.ownerId],
        () => {
          load();
        }
      );

      return {
        accept: ACCEPT,
        fileInput,
        documents,
        loading,
        uploading,
        downloadingId,
        emptyDescription,
        openFilePicker,
        handleFileSelect,
        downloadDocument,
        confirmDelete,
        kindLabel,
        fileIcon,
        formatSize,
        formatDate,
      };
    },
  };
</script>

<style scoped lang="scss">
  .entity-documents {
    padding: 1rem;

    &--embedded {
      padding: 0;
    }

    &__input {
      display: none;
    }

    &__add {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      width: 100%;
      margin-bottom: 0.75rem;
      padding: 0.875rem 1rem;
      background: white;
      border: 2px dashed var(--slate-200);
      border-radius: 0.75rem;
      color: var(--slate-500);
      font-size: 0.875rem;
      cursor: pointer;

      @include hover {
        border-color: var(--brand-300);
        color: var(--brand-600);
      }

      &:disabled {
        cursor: default;
        opacity: 0.7;
      }
    }

    &__loading {
      display: flex;
      justify-content: center;
      padding: 1.5rem 0;
    }

    &__spinner {
      width: 1.25rem;
      height: 1.25rem;
      border: 2px solid var(--slate-200);
      border-top-color: var(--brand-500);
      border-radius: 50%;
      animation: entity-documents-spin 0.7s linear infinite;
    }

    &__empty {
      display: flex;
      gap: 0.75rem;
      align-items: flex-start;
      padding: 0.25rem 0.25rem 0.5rem;
      color: var(--slate-500);
    }

    &__empty-title {
      margin: 0 0 0.125rem;
      color: var(--slate-700);
      font-size: 0.875rem;
      font-weight: 600;
    }

    &__empty-text {
      margin: 0;
      font-size: 0.8125rem;
      line-height: 1.4;
    }

    &__list {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
      margin: 0;
      padding: 0;
      list-style: none;
    }

    &__item {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      padding: 0.625rem 0.75rem;
      border: 1px solid var(--slate-200);
      border-radius: 0.75rem;
      background: white;
    }

    &__icon {
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      width: 2rem;
      height: 2rem;
      border-radius: 0.5rem;
      background: var(--slate-50);
      color: var(--slate-600);
    }

    &__main {
      display: flex;
      flex-direction: column;
      min-width: 0;
      flex: 1;
      gap: 0.125rem;
    }

    &__title {
      color: var(--slate-900);
      font-size: 0.875rem;
      font-weight: 600;
      @include ellipsis(1);
    }

    &__meta {
      color: var(--slate-500);
      font-size: 0.75rem;
      @include ellipsis(1);
    }

    &__actions {
      display: flex;
      align-items: center;
      gap: 0.25rem;
      flex-shrink: 0;
    }

    &__action {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 1.75rem;
      height: 1.75rem;
      border: 0;
      border-radius: 0.5rem;
      background: transparent;
      color: var(--slate-500);
      cursor: pointer;

      @include hover {
        background: var(--slate-100);
        color: var(--slate-800);
      }

      &:disabled {
        opacity: 0.5;
        cursor: default;
      }

      &--danger {
        @include hover {
          background: var(--red-50);
          color: var(--red-600);
        }
      }
    }
  }

  @keyframes entity-documents-spin {
    to {
      transform: rotate(360deg);
    }
  }
</style>
